import React, { useState, useEffect, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { LinearProgress, Typography } from "@material-ui/core";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  pollContainer: {
    width: "100%",
    minWidth: 250,
    maxWidth: 320,
    padding: "10px 8px 4px 8px",
    borderRadius: 8,
  },
  pollHeader: {
    display: "flex",
    alignItems: "flex-start",
    gap: 8,
    marginBottom: 2,
  },
  pollIconWrapper: {
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: theme.palette.type === "dark" ? "#00a884" : "#25D366",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    marginTop: 2,
  },
  pollIconSvg: {
    width: 18,
    height: 18,
    fill: "#fff",
  },
  pollTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  pollSubtitle: {
    fontSize: "0.73rem",
    color: theme.palette.type === "dark" ? "#8696a0" : "#667781",
    marginBottom: 10,
    marginTop: 2,
    paddingLeft: 40,
  },
  optionsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  optionRow: {
    borderRadius: 8,
    padding: "8px 10px 6px 10px",
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
    transition: "background-color 0.2s",
  },
  optionRowVoted: {
    backgroundColor: theme.palette.type === "dark" ? "rgba(0,168,132,0.12)" : "rgba(37,211,102,0.08)",
  },
  optionLabel: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  optionTextWrapper: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  checkCircle: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    backgroundColor: "#25D366",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  checkMark: {
    color: "#fff",
    fontSize: 12,
    fontWeight: 700,
  },
  emptyCircle: {
    width: 18,
    height: 18,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.15)"}`,
    flexShrink: 0,
  },
  optionText: {
    fontSize: "0.85rem",
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
    fontWeight: 400,
    wordBreak: "break-word",
  },
  optionTextVoted: {
    fontWeight: 600,
  },
  voteCount: {
    fontSize: "0.78rem",
    color: theme.palette.type === "dark" ? "#8696a0" : "#667781",
    fontWeight: 500,
    marginLeft: 8,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    gap: 3,
  },
  voteCountNumber: {
    fontWeight: 600,
    color: theme.palette.type === "dark" ? "#e9edef" : "#111b21",
  },
  progressRoot: {
    height: 5,
    borderRadius: 3,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)",
  },
  progressBarGreen: {
    borderRadius: 3,
    backgroundColor: "#25D366",
    transition: "width 0.6s ease",
  },
  progressBarGray: {
    borderRadius: 3,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
  },
  footer: {
    display: "flex",
    justifyContent: "center",
    marginTop: 10,
    paddingTop: 6,
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"}`,
  },
  footerText: {
    fontSize: "0.73rem",
    color: theme.palette.type === "dark" ? "#00a884" : "#128C7E",
    fontWeight: 500,
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
  votersList: {
    fontSize: "0.7rem",
    color: theme.palette.type === "dark" ? "#8696a0" : "#667781",
    marginTop: 3,
    lineHeight: 1.4,
  },
}));

// Parse poll data from message
const parsePollData = (message) => {
  // 1. Try dataJson (most reliable)
  if (message.dataJson) {
    try {
      const data = typeof message.dataJson === "string"
        ? JSON.parse(message.dataJson)
        : message.dataJson;

      if (data.pollData) {
        return {
          name: data.pollData.name,
          options: data.pollData.options || [],
          selectableCount: data.pollData.selectableCount ?? 0,
          votes: data.pollData.votes || {},
          totalVotes: data.pollData.totalVotes || 0,
          voters: data.pollData.voters || {},
        };
      }

      // Baileys proto format
      const pollMsg =
        data?.message?.pollCreationMessage ||
        data?.message?.pollCreationMessageV3 ||
        data?.message?.pollCreationMessageV2;
      if (pollMsg) {
        return {
          name: pollMsg.name,
          options: (pollMsg.options || []).map((o) => o.optionName || o),
          selectableCount: pollMsg.selectableCount ?? 0,
          votes: {},
          totalVotes: 0,
          voters: {},
        };
      }
    } catch (e) {
      // fall through
    }
  }

  // 2. Parse from body text
  const body = message.body || "";
  const lines = body.split("\n").filter((l) => l.trim());
  let name = "";
  const options = [];

  for (const line of lines) {
    // Match "📊 *Enquete:* Title" or "📊 Enquete: Title"
    const m = line.match(/Enquete:\*?\s*(.+)/);
    if (m) {
      name = m[1].replace(/\*$/g, "").trim();
      continue;
    }
    const optM = line.match(/^\d+\.\s+(.+)/);
    if (optM) {
      options.push(optM[1].trim());
    }
  }

  if (!name && options.length === 0) return null;

  return {
    name: name || "Enquete",
    options,
    selectableCount: 0,
    votes: {},
    totalVotes: 0,
    voters: {},
  };
};

const PollBarIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="white">
    <rect x="3" y="14" width="4" height="7" rx="1" />
    <rect x="10" y="9" width="4" height="12" rx="1" />
    <rect x="17" y="4" width="4" height="17" rx="1" />
  </svg>
);

const PollMessageCard = ({ message }) => {
  const classes = useStyles();
  const [pollData, setPollData] = useState(null);
  const [votes, setVotes] = useState({});
  const [totalVotes, setTotalVotes] = useState(0);
  const [voters, setVoters] = useState({});
  const [showVoters, setShowVoters] = useState(false);

  useEffect(() => {
    const data = parsePollData(message);
    if (data) {
      setPollData(data);
      setVotes(data.votes || {});
      setTotalVotes(data.totalVotes || 0);
      setVoters(data.voters || {});
    }
  }, [message, message.dataJson]);

  // Fetch votes from backend
  const fetchVotes = useCallback(async () => {
    if (!message.id) return;
    try {
      const { data } = await api.get(`/messages/${message.id}/poll-votes`);
      if (data && (data.totalVotes > 0 || Object.keys(data.votes || {}).length > 0)) {
        setVotes(data.votes || {});
        setTotalVotes(data.totalVotes || 0);
        if (data.voters) setVoters(data.voters);
      }
    } catch (e) {
      // Endpoint may not exist yet
    }
  }, [message.id]);

  useEffect(() => {
    fetchVotes();
    const interval = setInterval(fetchVotes, 15000);
    return () => clearInterval(interval);
  }, [fetchVotes]);

  if (!pollData) return null;

  const isMultiSelect = pollData.selectableCount === 0 || pollData.selectableCount > 1;

  return (
    <div className={classes.pollContainer}>
      {/* Header */}
      <div className={classes.pollHeader}>
        <div className={classes.pollIconWrapper}>
          <PollBarIcon />
        </div>
        <Typography className={classes.pollTitle}>
          {pollData.name}
        </Typography>
      </div>

      {/* Subtitle */}
      <div className={classes.pollSubtitle}>
        {isMultiSelect ? "⊙ Selecione uma ou mais opções" : "⊙ Selecione uma opção"}
      </div>

      {/* Options */}
      <div className={classes.optionsContainer}>
        {pollData.options.map((option, idx) => {
          const optVotes = votes[option] || 0;
          const pct = totalVotes > 0 ? Math.round((optVotes / totalVotes) * 100) : 0;
          const hasVotes = optVotes > 0;
          const optVoters = voters[option] || [];

          return (
            <div
              key={idx}
              className={`${classes.optionRow} ${hasVotes ? classes.optionRowVoted : ""}`}
            >
              <div className={classes.optionLabel}>
                <div className={classes.optionTextWrapper}>
                  {hasVotes ? (
                    <div className={classes.checkCircle}>
                      <span className={classes.checkMark}>✓</span>
                    </div>
                  ) : (
                    <div className={classes.emptyCircle} />
                  )}
                  <span className={`${classes.optionText} ${hasVotes ? classes.optionTextVoted : ""}`}>
                    {option}
                  </span>
                </div>
                {totalVotes > 0 && (
                  <span className={classes.voteCount}>
                    <span className={classes.voteCountNumber}>{optVotes}</span>
                  </span>
                )}
              </div>
              <LinearProgress
                variant="determinate"
                value={pct}
                classes={{
                  root: classes.progressRoot,
                  bar: hasVotes ? classes.progressBarGreen : classes.progressBarGray,
                }}
              />
              {showVoters && optVoters.length > 0 && (
                <div className={classes.votersList}>
                  {optVoters.join(", ")}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        {totalVotes > 0 ? (
          <span
            className={classes.footerText}
            onClick={() => setShowVoters(!showVoters)}
          >
            {showVoters ? "Ocultar votos" : `Mostrar votos · ${totalVotes} voto${totalVotes !== 1 ? "s" : ""}`}
          </span>
        ) : (
          <span className={classes.footerText} style={{ cursor: "default", opacity: 0.6 }}>
            Nenhum voto ainda
          </span>
        )}
      </div>
    </div>
  );
};

export default PollMessageCard;
